import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Table,
  Button, 
  Alert, 
  Modal,
  Form,
  Badge,
  InputGroup,
  Pagination,
  Dropdown
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faComments, 
  faEye, 
  faPlay,
  faStop,
  faSearch,
  faFilter,
  faRefresh,
  faSms,
  faUser,
  faCalendar,
  faChartLine,
  faPlus,
  faCheckCircle,
  faExclamationTriangle,
  faClock,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { useMsal } from '@azure/msal-react';
import smsApiService from '../services/smsApiService';
import { searchDirectoryPeople } from '../services/directorySearch';
import KarmaApiService from '../services/karmaApi';
import { isSmsAdminAccount } from '../utils/smsPermissions';
import { getDeliveryFailure, getDisplayStatus, getOptOut } from '../utils/smsDeliveryFailure';

const PLACEHOLDER_REGEX = /{{\s*([\w.]+)\s*}}/g;

const parseContextDataInput = (rawInput) => {
  const trimmed = (rawInput || '').trim();
  if (!trimmed) {
    return {};
  }

  // Allow JSON object input for power users.
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed);
      return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
    } catch (error) {
      // Fall back to key/value parsing below.
    }
  }

  const parsed = {};
  trimmed
    .split(/\r?\n|,/) 
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((pair) => {
      const separatorIndex = pair.includes('=') ? pair.indexOf('=') : pair.indexOf(':');
      if (separatorIndex <= 0) {
        return;
      }

      const key = pair.slice(0, separatorIndex).trim();
      const value = pair.slice(separatorIndex + 1).trim();
      if (key) {
        parsed[key] = value;
      }
    });

  return parsed;
};

const getInitialStageQuestion = (template) => {
  if (!template) {
    return '';
  }

  const stages = template.stages || template.Stages || [];
  if (!Array.isArray(stages) || stages.length === 0) {
    return '';
  }

  const initialStage = stages.find((stage) => stage?.IsInitialStage || stage?.isInitial) || stages[0];
  return initialStage?.Question || initialStage?.question || '';
};

const getInitialStage = (template) => {
  if (!template) {
    return null;
  }

  const stages = template.stages || template.Stages || [];
  if (!Array.isArray(stages) || stages.length === 0) {
    return null;
  }

  return stages.find((stage) => stage?.IsInitialStage || stage?.isInitial) || stages[0];
};

const resolveTemplatePlaceholders = (message, contextMap) => {
  const resolveTokenValue = (tokenName) => {
    if (!contextMap || !tokenName) {
      return undefined;
    }

    if (contextMap[tokenName] !== undefined && contextMap[tokenName] !== null && contextMap[tokenName] !== '') {
      return contextMap[tokenName];
    }

    const tokenLower = tokenName.toLowerCase();
    const directMatchKey = Object.keys(contextMap).find((key) => String(key).toLowerCase() === tokenLower);
    if (directMatchKey) {
      const directValue = contextMap[directMatchKey];
      if (directValue !== undefined && directValue !== null && directValue !== '') {
        return directValue;
      }
    }

    return undefined;
  };

  const unresolvedTokens = [];
  const finalMessage = (message || '').replace(PLACEHOLDER_REGEX, (fullMatch, tokenName) => {
    const value = resolveTokenValue(tokenName);
    if (value === null || value === undefined || value === '') {
      unresolvedTokens.push(tokenName);
      return fullMatch;
    }
    return String(value);
  });

  return {
    finalMessage,
    unresolvedTokens: [...new Set(unresolvedTokens)]
  };
};

const extractTemplatePlaceholders = (template) => {
  if (!template) {
    return [];
  }

  const stages = template.stages || template.Stages || [];
  const tokenSet = new Set();

  const scanText = (text) => {
    if (!text || typeof text !== 'string') {
      return;
    }

    let match;
    const regex = new RegExp(PLACEHOLDER_REGEX.source, 'g');
    while ((match = regex.exec(text)) !== null) {
      if (match[1]) {
        tokenSet.add(match[1]);
      }
    }
  };

  stages.forEach((stage) => {
    scanText(stage?.Question || stage?.question || '');

    const answers = stage?.answers || stage?.Answers || [];
    answers.forEach((answer) => {
      scanText(answer?.ResponseMessage || answer?.responseMessage || answer?.Response || answer?.response || '');
    });

    const defaultResponse = stage?.defaultResponse || stage?.DefaultResponse;
    if (defaultResponse && typeof defaultResponse === 'object') {
      scanText(defaultResponse.message);
      scanText(defaultResponse.retryMessage);
      scanText(defaultResponse.DefaultMessage);
      scanText(defaultResponse.RetryMessage);
    }
  });

  return Array.from(tokenSet).sort();
};

const extractTemplatePlaceholdersOutsideInitialQuestion = (template) => {
  if (!template) {
    return [];
  }

  const stages = template.stages || template.Stages || [];
  const initialStage = getInitialStage(template);
  const initialStageId = initialStage?.Id || initialStage?.id || null;
  const tokenSet = new Set();

  const scanText = (text) => {
    if (!text || typeof text !== 'string') {
      return;
    }

    let match;
    const regex = new RegExp(PLACEHOLDER_REGEX.source, 'g');
    while ((match = regex.exec(text)) !== null) {
      if (match[1]) {
        tokenSet.add(match[1]);
      }
    }
  };

  stages.forEach((stage) => {
    const stageId = stage?.Id || stage?.id || null;

    // Exclude only the original initial question text itself.
    if (!(stageId === initialStageId && initialStageId !== null)) {
      scanText(stage?.Question || stage?.question || '');
    }

    const answers = stage?.answers || stage?.Answers || [];
    answers.forEach((answer) => {
      scanText(answer?.ResponseMessage || answer?.responseMessage || answer?.Response || answer?.response || '');
    });

    const defaultResponse = stage?.defaultResponse || stage?.DefaultResponse;
    if (defaultResponse && typeof defaultResponse === 'object') {
      scanText(defaultResponse.message);
      scanText(defaultResponse.retryMessage);
      scanText(defaultResponse.DefaultMessage);
      scanText(defaultResponse.RetryMessage);
    }
  });

  return Array.from(tokenSet).sort();
};

const prettifyTokenLabel = (token) => {
  if (!token) {
    return 'Value';
  }

  const lastSegment = token.split('.').pop() || token;
  const spaced = lastSegment
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_.-]/g, ' ')
    .trim();

  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const buildTokenLookup = (placeholderTokens = []) => {
  const tokenLookup = {};
  placeholderTokens.forEach((token) => {
    tokenLookup[token.toLowerCase()] = token;
  });
  return tokenLookup;
};

const parseRecipientsInput = (rawInput, placeholderTokens = []) => {
  const trimmed = (rawInput || '').trim();
  if (!trimmed) {
    return { recipients: [], format: 'empty' };
  }

  const tokenLookup = buildTokenLookup(placeholderTokens);

  if (trimmed.startsWith('[')) {
    let parsed;
    try {
      parsed = JSON.parse(trimmed);
    } catch (error) {
      throw new Error('Recipient JSON is invalid. Please check commas and quotes.');
    }

    if (!Array.isArray(parsed)) {
      throw new Error('Recipient JSON must be an array of objects.');
    }

    const recipients = parsed
      .map((item, sourceIndex) => {
        if (!item || typeof item !== 'object' || Array.isArray(item)) {
          return null;
        }

        const phoneNumber = String(item.number || item.phoneNumber || item.phone || '').trim();
        if (!phoneNumber) {
          return null;
        }

        const placeholderContext = {};
        const additionalContext = {};
        Object.entries(item).forEach(([key, value]) => {
          const normalizedKey = String(key || '').trim();
          if (!normalizedKey) {
            return;
          }

          const normalizedValue = String(value ?? '').trim();
          if (normalizedValue) {
            additionalContext[normalizedKey] = normalizedValue;
          }

          if (['number', 'phone', 'phoneNumber'].includes(normalizedKey.toLowerCase())) {
            return;
          }

          const canonicalToken = tokenLookup[String(key || '').toLowerCase()];
          if (!canonicalToken) {
            return;
          }
          if (normalizedValue) {
            placeholderContext[canonicalToken] = normalizedValue;
          }
        });

        return {
          phoneNumber,
          placeholderContext,
          additionalContext,
          sourceIndex
        };
      })
      .filter(Boolean);

    return { recipients, format: 'json' };
  }

  const recipients = trimmed
    .split(',')
    .map((num) => num.trim())
    .filter((num) => num.length > 0)
    .map((phoneNumber) => ({ phoneNumber, placeholderContext: {}, additionalContext: {} }));

  return { recipients, format: 'csv' };
};

const mapDefaultsToTemplateTokens = (defaultSource = {}, placeholderTokens = []) => {
  const tokenLookup = buildTokenLookup(placeholderTokens);
  const mapped = {};

  Object.entries(defaultSource || {}).forEach(([key, value]) => {
    const canonicalToken = tokenLookup[String(key || '').toLowerCase()];
    if (!canonicalToken) {
      return;
    }

    const normalizedValue = String(value ?? '').trim();
    if (normalizedValue) {
      mapped[canonicalToken] = normalizedValue;
    }
  });

  return mapped;
};

const evaluateRecipientValidation = ({
  rawRecipientsInput,
  placeholderTokens,
  defaultSource
}) => {
  const requiredTokens = Array.isArray(placeholderTokens) ? placeholderTokens : [];

  let parsed;
  try {
    parsed = parseRecipientsInput(rawRecipientsInput, requiredTokens);
  } catch (error) {
    return {
      isValid: false,
      parseError: error.message,
      recipientsCount: 0,
      missingDefaultTokens: requiredTokens,
      recipientsMissingTokens: [],
      format: 'invalid'
    };
  }

  const recipients = parsed.recipients || [];
  const mappedDefaults = mapDefaultsToTemplateTokens(defaultSource, requiredTokens);
  const missingDefaultTokens = requiredTokens.filter((token) => !mappedDefaults[token]);
  const hasAllDefaults = missingDefaultTokens.length === 0;

  const recipientsMissingTokens = recipients
    .map((recipient, idx) => {
      const missingTokens = requiredTokens.filter(
        (token) => !recipient.placeholderContext?.[token]
      );
      return {
        recipientIndex: (recipient.sourceIndex ?? idx) + 1,
        missingTokens
      };
    })
    .filter((item) => item.missingTokens.length > 0);

  const jsonHasFullCoverage = parsed.format === 'json' && recipientsMissingTokens.length === 0;
  const hasRecipients = recipients.length > 0;

  return {
    isValid: hasRecipients && (hasAllDefaults || jsonHasFullCoverage),
    parseError: null,
    recipientsCount: recipients.length,
    missingDefaultTokens,
    recipientsMissingTokens,
    format: parsed.format,
    hasAllDefaults,
    jsonHasFullCoverage
  };
};

const extractPlaceholderTokensFromText = (text) => {
  if (!text || typeof text !== 'string') {
    return [];
  }

  const tokens = new Set();
  const regex = new RegExp(PLACEHOLDER_REGEX.source, 'g');
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match[1]) {
      tokens.add(match[1]);
    }
  }

  return Array.from(tokens);
};

/* ------------------------------------------------------------------ *
 * Karma contact lookup helpers                                        *
 *                                                                     *
 * A conversation only writes back to Karma if contextData.contactId is *
 * set at start. Nothing matches on phone number anywhere, so without   *
 * an id the SMS sends but the CRM never hears about it.                *
 * ------------------------------------------------------------------ */

const CONTACT_ID_KEYS = ['contactid', 'karmacontactid', 'karma_contact_id'];

/**
 * The statuses a conversation can hold. "Active" is the only one the automated
 * flow answers on, so moving a conversation off Active is what stops it
 * replying - see processIncomingSMS in the API.
 */
const SMS_STATUSES = [
  { value: 'Active',        label: 'Active',               note: 'The automated flow will reply to the customer.' },
  { value: 'AwaitingHuman', label: 'Open (Human Managed)', note: 'Still open, but the flow will not reply. A colleague handles it.' },
  { value: 'Completed',     label: 'Completed',            note: 'Closed. The flow will not reply.' },
  { value: 'Abandoned',     label: 'Abandoned',            note: 'Closed as gone-away. The flow will not reply.' },
  { value: 'Error',         label: 'Error',                note: 'Something went wrong. The flow will not reply.' }
];

/** What the Conversations screen shows before anyone touches the filter. */
// Error is included so texts that failed (for example an invalid number) show
// next to the open conversations, with a red Failed tile, rather than being hidden.
const DEFAULT_STATUS_FILTER = ['Active', 'AwaitingHuman', 'Error'];

const isDefaultStatusFilter = (list) => {
  const a = (list || []).filter(Boolean).slice().sort().join(',');
  return a === DEFAULT_STATUS_FILTER.slice().sort().join(',');
};

const statusLabel = (value) => SMS_STATUSES.find((item) => item.value === value)?.label || value || 'Unknown';

const normalizeKarmaResults = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const karmaFirstValue = (value) => (Array.isArray(value) ? String(value[0] ?? '') : String(value ?? ''));

const karmaContactName = (contact) => {
  if (!contact || typeof contact !== 'object') return '';
  const first = karmaFirstValue(contact.first_name || contact.firstName).trim();
  const last = karmaFirstValue(contact.last_name || contact.lastName).trim();
  return `${first} ${last}`.trim() || karmaFirstValue(contact.label).trim();
};

/**
 * Reduce a number to UK national format so 07977133028, +44 7977 133028
 * and 447977133028 are all recognised as the same mobile.
 */
const toUkNational = (value) => {
  let digits = String(value || '').replace(/[^\d+]/g, '');
  if (digits.startsWith('+')) digits = digits.slice(1);
  if (digits.startsWith('0044')) digits = digits.slice(4);
  else if (digits.startsWith('44')) digits = digits.slice(2);
  if (digits.startsWith('0')) return digits;
  if (/^7\d{9}$/.test(digits)) return `0${digits}`;
  return digits;
};

/** UK mobiles are 07 followed by nine digits. Everything else cannot receive SMS. */
const isUkMobile = (value) => /^07\d{9}$/.test(toUkNational(value));

/** Prefer a UK mobile over a landline, same rule the Booking tab uses. */
const karmaPickMobile = (contact) => {
  const phones = Array.isArray(contact?.phone_numbers)
    ? contact.phone_numbers
    : Array.isArray(contact?.phoneNumbers)
      ? contact.phoneNumbers
      : [];
  const numbers = phones.map((item) => String(item?.number || '').trim()).filter(Boolean);
  if (!numbers.length) return '';
  return numbers.find(isUkMobile) || numbers[0];
};

/**
 * Every number on the record, labelled as the Booking tab labels them
 * ("mobile: 07977133028"), mobiles first so the default is sensible.
 */
const karmaPhoneOptions = (contact) => {
  const phones = Array.isArray(contact?.phone_numbers)
    ? contact.phone_numbers
    : Array.isArray(contact?.phoneNumbers)
      ? contact.phoneNumbers
      : [];

  const options = phones
    .map((item, idx) => {
      const number = String(item?.number || '').trim();
      if (!number) return null;
      const tag = String(item?.label || item?.type || `Phone ${idx + 1}`).trim();
      const mobile = isUkMobile(number);
      return {
        key: `${idx}-${number}`,
        label: `${tag}: ${number}${mobile ? '' : '  (not a mobile)'}`,
        number,
        isMobile: mobile
      };
    })
    .filter(Boolean);

  return [
    ...options.filter((item) => item.isMobile),
    ...options.filter((item) => !item.isMobile)
  ];
};

/** Postcode from the contact's first address, for {{postcode}}. */
const karmaPostcode = (contact) => {
  const addresses = Array.isArray(contact?.addresses) ? contact.addresses : [];
  return String(addresses[0]?.postal_code || addresses[0]?.postcode || '').trim();
};

/** First line of the contact's first address, for {{address1}}. */
const karmaAddressLine1 = (contact) => {
  const addresses = Array.isArray(contact?.addresses) ? contact.addresses : [];
  const first = addresses[0];
  return String(first?.street || first?.address1 || first?.line1 || '').trim();
};

const karmaAddressPreview = (contact) => {
  const addresses = Array.isArray(contact?.addresses) ? contact.addresses : [];
  const first = addresses[0];
  if (!first) return '';
  return [first.street, first.city, first.postal_code]
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .join(', ');
};

const rowHasContactId = (row) => Object.entries(row || {}).some(
  ([key, value]) => CONTACT_ID_KEYS.includes(String(key).toLowerCase()) && String(value ?? '').trim()
);

const buildExampleRecipient = (placeholderTokens = []) => {
  // contactId first: it is what links the conversation back to the Karma
  // record. Without it the text still sends, but nothing reaches the CRM.
  const recipient = { contactId: '48231', number: '+447700900123' };
  placeholderTokens.forEach((token) => {
    recipient[token] = `Sample ${prettifyTokenLabel(token)}`;
  });
  return recipient;
};

const isManagedBookingTemplate = (template) => {
  const name = String(template?.Name || template?.name || '').trim();
  const description = String(template?.Description || template?.description || '').trim();
  return name.startsWith('Karma Text Booking Flow') || description.startsWith('Managed placeholder template for ');
};

const parseCsvLine = (line) => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      values.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  values.push(current.trim());
  return values;
};

const escapeCsvValue = (value) => {
  const text = String(value ?? '');
  if (text.includes(',') || text.includes('"') || text.includes('\n')) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const convertRecipientsToCsv = (recipients = []) => {
  if (!Array.isArray(recipients) || recipients.length === 0) {
    return 'number';
  }

  const headers = Array.from(
    recipients.reduce((set, row) => {
      Object.keys(row || {}).forEach((key) => set.add(key));
      return set;
    }, new Set())
  );

  const lines = [headers.join(',')];
  recipients.forEach((row) => {
    const line = headers.map((header) => escapeCsvValue(row?.[header] ?? '')).join(',');
    lines.push(line);
  });

  return lines.join('\n');
};

const parseCsvToRecipients = (csvText) => {
  const lines = (csvText || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    throw new Error('CSV must include a header row and at least one data row.');
  }

  const headers = parseCsvLine(lines[0]);
  if (!headers.length) {
    throw new Error('CSV header row is empty.');
  }

  const recipients = lines.slice(1).map((line) => {
    const fields = parseCsvLine(line);
    const row = {};
    headers.forEach((header, idx) => {
      const key = String(header || '').trim();
      if (!key) {
        return;
      }
      row[key] = fields[idx] ?? '';
    });
    return row;
  });

  return recipients;
};

const triggerTextFileDownload = (filename, content, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

const parseConversationContext = (conversation) => {
  const source = conversation?.ContextData || conversation?.contextData || conversation?.Metadata || conversation?.metadata;
  if (!source) {
    return {};
  }

  if (typeof source === 'object') {
    return source;
  }

  try {
    const parsed = JSON.parse(source);
    if (!parsed || typeof parsed !== 'object') {
      return {};
    }

    // Some payloads wrap context under contextData/ContextData.
    if (parsed.contextData && typeof parsed.contextData === 'object') {
      return parsed.contextData;
    }
    if (parsed.ContextData && typeof parsed.ContextData === 'object') {
      return parsed.ContextData;
    }

    return parsed;
  } catch (error) {
    return {};
  }
};

const getConversationCustomerName = (conversation) => {
  const context = parseConversationContext(conversation);
  const directName = String(
    conversation?.CustomerName
    || conversation?.customerName
    || conversation?.ContactName
    || conversation?.contactName
    || conversation?.FullName
    || conversation?.fullName
    || context.customerName
    || context.fullName
    || context.FullName
    || ''
  ).trim();

  if (directName) {
    return directName;
  }

  const firstName = String(
    conversation?.FirstName
    || conversation?.firstName
    || conversation?.first_name
    || context.firstName
    || context.first_name
    || context.FirstName
    || ''
  ).trim();

  const lastName = String(
    conversation?.LastName
    || conversation?.lastName
    || conversation?.last_name
    || context.lastName
    || context.last_name
    || context.LastName
    || ''
  ).trim();

  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (fullName) {
    return fullName;
  }

  const title = String(context.title || context.Title || '').trim();
  const titled = [title, lastName].filter(Boolean).join(' ').trim();
  if (titled) {
    return titled;
  }

  const contactId = Number.parseInt(conversation?.ContactId || conversation?.contactId || context.contactId, 10);
  if (Number.isFinite(contactId)) {
    return `Contact ${contactId}`;
  }

  return 'Unknown Customer';
};

const isDeliveredToHandsetMessage = (value) => /message\s+delivered\s+to\s+the\s+handset/i.test(String(value || ''));

const SMSConversationManager = () => {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount() || instance.getAllAccounts?.()[0] || null;
  const isSmsAdmin = isSmsAdminAccount(activeAccount);
  const [searchParams, setSearchParams] = useState(() => new URLSearchParams(window.location.search));
  const consumedConversationIdRef = useRef(null);
  const conversationHistoryRef = useRef(null);
  const shouldStickToBottomRef = useRef(true);
  const previousMessageCountRef = useRef(0);
  const [conversations, setConversations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showStartModal, setShowStartModal] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [keepFlowRunning, setKeepFlowRunning] = useState(false);
  const [statusDraft, setStatusDraft] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });
  
  // Filters and pagination
  const [filters, setFilters] = useState({
    status: DEFAULT_STATUS_FILTER,
    templateId: '',
    phoneNumber: '',
    dateFrom: '',
    dateTo: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 20;



  useEffect(() => {
    if (isSmsAdmin) {
      loadTemplates();
    }
  }, [isSmsAdmin]);

  useEffect(() => {
    loadConversations();
  }, [currentPage, filters]);

  useEffect(() => {
    const conversationIdFromUrl = searchParams.get('conversationId');
    if (!conversationIdFromUrl) {
      consumedConversationIdRef.current = null;
      return;
    }

    if (consumedConversationIdRef.current === String(conversationIdFromUrl)) {
      return;
    }

    const match = conversations.find((conversation) => String(conversation.Id) === String(conversationIdFromUrl));
    if (match && !showHistoryModal) {
      consumedConversationIdRef.current = String(conversationIdFromUrl);
      viewConversationHistory(match);
    }
  }, [conversations, searchParams, showHistoryModal]);

  const handleHistoryModalClose = useCallback(() => {
    setShowHistoryModal(false);
    shouldStickToBottomRef.current = true;
    previousMessageCountRef.current = 0;

    const nextParams = new URLSearchParams(window.location.search);
    if (!nextParams.has('conversationId')) {
      return;
    }

    nextParams.delete('conversationId');
    const nextQuery = nextParams.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash || ''}`;
    window.history.replaceState({}, '', nextUrl);
    setSearchParams(new URLSearchParams(window.location.search));
  }, []);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const statusList = Array.isArray(filters.status) ? filters.status : [filters.status].filter(Boolean);
      const response = await smsApiService.getConversations({
        ...filters,
        // The API accepts one status or a comma-separated list. The default view
        // also asks for Completed, so opt-outs (which the API closes as Completed)
        // stay on screen as "OPT OUT (Action Required)"; other Completed ones are
        // dropped in visibleConversations.
        status: (isDefaultStatusFilter(statusList) ? [...statusList, 'Completed'] : statusList).join(','),
        page: currentPage,
        limit: itemsPerPage
      }, instance);
      
      // Handle both array response and object with conversations property
      const conversationsData = Array.isArray(response.data) ? response.data : (response.data.conversations || []);
      
      setConversations(conversationsData);
      
      const total = Array.isArray(response.data) ? response.data.length : (response.data.total || conversationsData.length);
      setTotalPages(Math.ceil(total / itemsPerPage));
    } catch (error) {
      console.error('Error loading conversations:', error);
      showAlert('Error loading conversations', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    if (!isSmsAdmin) {
      setTemplates([]);
      return;
    }

    try {
      const response = await smsApiService.getTemplates(instance);
      setTemplates(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading templates:', error);
      showAlert('Error loading templates', 'danger');
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: 'success' }), 3000);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusLabel = (status) => {
    const labels = {
      AwaitingHuman: 'Open (Human Managed)'
    };
    return labels[status] || status;
  };

  /** A red "Failed" tile when the text never reached the customer. */
  const getConversationBadge = (conversation) => {
    const shown = getDisplayStatus(conversation);
    if (shown) return <Badge bg={shown.variant}>{shown.label}</Badge>;
    return getStatusBadge(conversation?.Status || conversation?.status || 'Unknown');
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Active': 'primary',
      'AwaitingHuman': 'warning',
      'Completed': 'success',
      'Abandoned': 'warning',
      'Error': 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{getStatusLabel(status)}</Badge>;
  };

  const templateFilterOptions = useMemo(() => {
    return templates.filter((template) => !isManagedBookingTemplate(template));
  }, [templates]);

  const visibleConversations = useMemo(() => {
    const selectedTemplateId = String(filters.templateId || '').trim();
    const textQuery = String(searchTerm || '').trim().toLowerCase();

    const defaultView = isDefaultStatusFilter(Array.isArray(filters.status) ? filters.status : [filters.status]);
    return conversations.filter((conversation) => {
      if (defaultView && (conversation?.Status || conversation?.status) === 'Completed' && !getOptOut(conversation)) {
        return false;
      }
      if (selectedTemplateId && String(conversation?.TemplateId || '').trim() !== selectedTemplateId) {
        return false;
      }

      if (!textQuery) {
        return true;
      }

      const phone = String(conversation?.PhoneNumber || conversation?.phoneNumber || '').toLowerCase();
      const name = getConversationCustomerName(conversation).toLowerCase();
      return phone.includes(textQuery) || name.includes(textQuery);
    });
  }, [conversations, filters.templateId, filters.status, searchTerm]);

  const getDeliveryStatusIcon = (deliveryStatus) => {
    switch (deliveryStatus?.toLowerCase()) {
      case 'delivered':
        return <FontAwesomeIcon icon={faCheckCircle} className="text-success" title="Delivered" />;
      case 'failed':
        return <FontAwesomeIcon icon={faTimesCircle} className="text-danger" title="Failed" />;
      case 'pending':
        return <FontAwesomeIcon icon={faClock} className="text-warning" title="Pending" />;
      default:
        return <FontAwesomeIcon icon={faClock} className="text-muted" title="Unknown" />;
    }
  };

  const loadSelectedConversationHistory = useCallback(async (conversation, options = {}) => {
    const shouldShowModal = options.showModal !== false;
    const shouldSetLoading = options.setLoading !== false;

    if (!conversation?.Id) {
      return null;
    }

    if (shouldSetLoading) {
      setLoading(true);
    }

    try {
      // Use the new endpoint that includes delivery stats
      const response = await smsApiService.getConversationWithDelivery(conversation.Id, instance);

      // Extract messages array from the conversation object
      const messages = response.data?.messages || [];
      const deliveryStats = response.data?.deliveryStats || null;

      // The API returns the conversation's fields at the TOP LEVEL
      // ({ ...conversation.toJSON(), deliveryStats }), not nested under a
      // "conversation" key. The old code only merged response.data.conversation,
      // which is always undefined, so Status and LastResponseAt kept whatever
      // they held when the list was last loaded - the View screen could show
      // "Active" long after the flow had handed the conversation to a human.
      // Both shapes are handled here in case the endpoint ever changes.
      const {
        messages: _ignoredMessages,
        deliveryStats: _ignoredStats,
        conversation: nestedConversation,
        ...topLevelFields
      } = response.data || {};

      const refreshedConversation = {
        ...conversation,
        ...topLevelFields,
        ...(nestedConversation || {}),
        deliveryStats
      };
      
      setConversationHistory(messages);
      setSelectedConversation(refreshedConversation);
      if (shouldShowModal) {
        setShowHistoryModal(true);
      }

      return refreshedConversation;
    } catch (error) {
      console.error('Error loading conversation history:', error);
      // Fallback to regular conversation history if delivery endpoint fails
      try {
        const response = await smsApiService.getConversationHistory(conversation.Id, instance);
        const messages = response.data?.messages || [];
        setConversationHistory(messages);
        setSelectedConversation(conversation);
        if (shouldShowModal) {
          setShowHistoryModal(true);
        }
        return conversation;
      } catch (fallbackError) {
        if (options.showAlert !== false) {
          showAlert('Error loading conversation history', 'danger');
        }
        return null;
      }
    } finally {
      if (shouldSetLoading) {
        setLoading(false);
      }
    }
  }, [instance]);

  const viewConversationHistory = async (conversation) => {
    shouldStickToBottomRef.current = true;
    previousMessageCountRef.current = 0;
    setSelectedConversation(conversation);
    await loadSelectedConversationHistory(conversation, { showModal: true, setLoading: true });
  };

  const handleConversationScroll = useCallback(() => {
    const container = conversationHistoryRef.current;
    if (!container) {
      return;
    }

    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    shouldStickToBottomRef.current = distanceFromBottom <= 80;
  }, []);

  useEffect(() => {
    if (!showHistoryModal || !selectedConversation?.Id) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      loadSelectedConversationHistory(selectedConversation, {
        showModal: false,
        setLoading: false,
        showAlert: false
      });
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loadSelectedConversationHistory, selectedConversation, showHistoryModal]);

  useEffect(() => {
    if (!showHistoryModal || !conversationHistoryRef.current) {
      return;
    }

    const messageCount = Array.isArray(conversationHistory) ? conversationHistory.length : 0;
    const hasPreviousMessages = previousMessageCountRef.current > 0;
    const hasNewMessages = messageCount > previousMessageCountRef.current;
    const shouldAutoScroll = !hasPreviousMessages || hasNewMessages;

    if (shouldAutoScroll && shouldStickToBottomRef.current) {
      window.requestAnimationFrame(() => {
        if (!conversationHistoryRef.current) {
          return;
        }

        conversationHistoryRef.current.scrollTop = conversationHistoryRef.current.scrollHeight;
      });
    }

    previousMessageCountRef.current = messageCount;
  }, [conversationHistory, showHistoryModal]);

  const handleReplySend = async () => {
    const message = String(replyMessage || '').trim();
    if (!selectedConversation?.Id) {
      showAlert('Select a conversation first.', 'warning');
      return;
    }

    if (!message) {
      showAlert('Enter a reply message first.', 'warning');
      return;
    }

    setSendingReply(true);
    try {
      const response = await smsApiService.sendManualConversationReply(
        selectedConversation.Id,
        message,
        instance,
        { keepFlowRunning }
      );
      setReplyMessage('');
      showAlert(
        response?.data?.message
          || (response?.data?.flowStopped
            ? 'Reply sent. The automated flow has been stopped for this conversation.'
            : 'Reply sent.'),
        'success'
      );
      await loadSelectedConversationHistory(selectedConversation, { showModal: false, setLoading: false });
      await loadConversations();
    } catch (error) {
      console.error('Error sending manual SMS reply:', error);
      showAlert(error.response?.data?.error || error.message || 'Failed to send reply', 'danger');
    } finally {
      setSendingReply(false);
    }
  };



  useEffect(() => {
    setStatusDraft(selectedConversation?.Status || selectedConversation?.status || '');
    setKeepFlowRunning(false);
  }, [selectedConversation?.Id, selectedConversation?.Status, selectedConversation?.status]);

  const handleStatusSave = async () => {
    const nextStatus = String(statusDraft || '').trim();
    const currentStatus = selectedConversation?.Status || selectedConversation?.status;

    if (!selectedConversation?.Id || !nextStatus || nextStatus === currentStatus) {
      return;
    }

    setSavingStatus(true);
    try {
      const response = await smsApiService.updateConversationStatus(selectedConversation.Id, nextStatus, instance);
      setSelectedConversation((prev) => (prev ? { ...prev, Status: nextStatus, status: nextStatus } : prev));
      showAlert(response?.data?.message || `Status changed to ${statusLabel(nextStatus)}.`, 'success');
      await loadConversations();
    } catch (error) {
      console.error('Error updating conversation status:', error);
      showAlert(error.response?.data?.error || error.message || 'Failed to change status', 'danger');
      setStatusDraft(currentStatus || '');
    } finally {
      setSavingStatus(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const toggleStatusFilter = (value) => {
    setFilters((prev) => {
      const current = Array.isArray(prev.status) ? prev.status : (prev.status ? [prev.status] : []);
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, status: next };
    });
    setCurrentPage(1);
  };

  const setAllStatusFilters = (selectAll) => {
    setFilters((prev) => ({ ...prev, status: selectAll ? SMS_STATUSES.map((item) => item.value) : [] }));
    setCurrentPage(1);
  };

  const selectedStatuses = Array.isArray(filters.status)
    ? filters.status
    : (filters.status ? [filters.status] : []);

  const statusFilterSummary = selectedStatuses.length === 0
    ? 'All statuses'
    : selectedStatuses.length === SMS_STATUSES.length
      ? 'All statuses'
      : selectedStatuses.length === 1
        ? statusLabel(selectedStatuses[0])
        : `${selectedStatuses.length} statuses selected`;

  const clearFilters = () => {
    setFilters({
      status: DEFAULT_STATUS_FILTER,
      templateId: '',
      phoneNumber: '',
      dateFrom: '',
      dateTo: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const conversationHistoryModal = (
    <Modal show={showHistoryModal} onHide={handleHistoryModalClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faComments} className="me-2" />
          Conversation History: {getConversationCustomerName(selectedConversation)} ({selectedConversation?.PhoneNumber || selectedConversation?.phoneNumber || 'No Phone'})
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedConversation && (
          <div className="mb-3">
            <Row>
              <Col md={6}>
                <strong>Status:</strong> {getConversationBadge(selectedConversation)}
                <div className="d-flex align-items-center gap-2 mt-2">
                  <Form.Select
                    size="sm"
                    style={{ maxWidth: '220px' }}
                    value={statusDraft}
                    onChange={(e) => setStatusDraft(e.target.value)}
                    disabled={savingStatus}
                  >
                    {SMS_STATUSES.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </Form.Select>
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={
                      savingStatus
                      || !statusDraft
                      || statusDraft === (selectedConversation.Status || selectedConversation.status)
                    }
                    onClick={handleStatusSave}
                  >
                    {savingStatus ? 'Saving...' : 'Change status'}
                  </Button>
                </div>
                <Form.Text className="text-muted d-block">
                  {SMS_STATUSES.find((item) => item.value === statusDraft)?.note || ''}
                </Form.Text>
              </Col>
              <Col md={6}>
                <strong>Template:</strong> {selectedConversation.template?.Name || 'Unknown Template'}
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <strong>Started:</strong> {formatDate(selectedConversation.StartedAt)}
              </Col>
              <Col md={6}>
                <strong>Last Response:</strong> {selectedConversation.LastResponseAt ? formatDate(selectedConversation.LastResponseAt) : 'None'}
              </Col>
            </Row>
            {selectedConversation.deliveryStats && (
              <Row className="mt-2">
                <Col md={12}>
                  <strong>Delivery Status:</strong>{' '}
                  <Badge bg="success" className="me-1">✓ {selectedConversation.deliveryStats.delivered} Delivered</Badge>
                  {selectedConversation.deliveryStats.pending > 0 && (
                    <Badge bg="warning" className="me-1">⏳ {selectedConversation.deliveryStats.pending} Pending</Badge>
                  )}
                  {selectedConversation.deliveryStats.failed > 0 && (
                    <Badge bg="danger" className="me-1">✗ {selectedConversation.deliveryStats.failed} Failed</Badge>
                  )}
                </Col>
              </Row>
            )}
            {getOptOut(selectedConversation) && (
              <Row className="mt-2">
                <Col md={12}>
                  <Alert variant="danger" className="mb-0 py-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <strong>OPT OUT (Action Required).</strong> The customer replied "{getOptOut(selectedConversation).text}"
                    {getOptOut(selectedConversation).at ? ` on ${formatDate(getOptOut(selectedConversation).at)}` : ''}.
                    {' '}The SMS system will not text this number again. Please update their contact preferences and contact them another way if needed.
                    <div className="small mt-1">Once it's dealt with, set the status to Abandoned to take it off the main list.</div>
                  </Alert>
                </Col>
              </Row>
            )}
            {getDeliveryFailure(selectedConversation) && (
              <Row className="mt-2">
                <Col md={12}>
                  <Alert variant="danger" className="mb-0 py-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <strong>Failed: text not delivered.</strong> {getDeliveryFailure(selectedConversation).explanation}
                    <div className="small mt-1">Reason from the SMS provider: {getDeliveryFailure(selectedConversation).reason}</div>
                  </Alert>
                </Col>
              </Row>
            )}
            {selectedConversation.ErrorMessage && !getDeliveryFailure(selectedConversation) && (
              <Row className="mt-2">
                <Col md={12}>
                  <Alert variant="danger" className="mb-0 py-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <strong>Error:</strong> {selectedConversation.ErrorMessage}
                  </Alert>
                </Col>
              </Row>
            )}
          </div>
        )}
        
        <div ref={conversationHistoryRef} className="conversation-history" onScroll={handleConversationScroll} style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {conversationHistory.length === 0 ? (
            <div className="text-center text-muted py-4">
              <FontAwesomeIcon icon={faComments} size="3x" className="mb-3 opacity-50" />
              <p>No messages in this conversation yet.</p>
            </div>
          ) : (
            conversationHistory.map((message, index) => (
              <div key={index} className={`mb-3 d-flex ${message.MessageType === 'Sent' ? 'justify-content-start' : 'justify-content-end'}`}>
                <div className="d-flex flex-column" style={{ maxWidth: '75%' }}>
                  <div className={`sms-bubble p-3 rounded-3 ${message.MessageType === 'Sent' ? 'bg-primary text-white' : 'bg-light border'}`}>
                    <div className="message-content" style={{ whiteSpace: 'pre-line' }}>{message.MessageText}</div>
                  </div>
                  <small className={`text-muted mt-1 ${message.MessageType === 'Sent' ? 'align-self-start' : 'align-self-end'}`}>
                    <FontAwesomeIcon 
                      icon={message.MessageType === 'Sent' ? faPlay : faUser} 
                      className="me-1" 
                    />
                    {message.MessageType === 'Sent' ? 'System' : 'Customer'} • {formatDate(message.SentAt)}
                    {message.MessageType === 'Sent' && message.deliveryStatus && (
                      <span className="ms-2">
                        {getDeliveryStatusIcon(message.deliveryStatus)}
                      </span>
                    )}
                    {message.MessageType === 'Received' && message.IsValidResponse !== null && (
                      <Badge bg="success" className="ms-2">
                        {message.IsValidResponse ? '✓ Valid Reply' : '✓ Received'}
                      </Badge>
                    )}
                  </small>
                  {message.ErrorMessage && (
                    <small className={`${isDeliveredToHandsetMessage(message.ErrorMessage) ? 'text-success' : 'text-danger'} mt-1 align-self-start`}>
                      <FontAwesomeIcon icon={isDeliveredToHandsetMessage(message.ErrorMessage) ? faCheckCircle : faExclamationTriangle} className="me-1" />
                      {message.ErrorMessage}
                    </small>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <Card className="mt-3 border-primary">
          <Card.Header className="bg-light">
            <FontAwesomeIcon icon={faSms} className="me-2" />
            Human Reply
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-2">
              <Form.Label>Send a manual SMS reply</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="Type the reply you want to send to the customer..."
              />
            </Form.Group>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <Form.Check
                type="checkbox"
                id="keep-flow-running"
                className="mb-0"
                label="Keep the automated flow running"
                checked={keepFlowRunning}
                onChange={(e) => setKeepFlowRunning(e.target.checked)}
              />
              <Button
                variant="primary"
                onClick={handleReplySend}
                disabled={sendingReply || !selectedConversation || !replyMessage.trim()}
              >
                {sendingReply ? 'Sending...' : 'Send Reply'}
              </Button>
            </div>
            <Form.Text className="text-muted d-block mt-1">
              {keepFlowRunning
                ? 'The flow will reply to the customer as well as you, and will be restarted if it had already stopped. Use with care.'
                : 'Sending hands this conversation over to you - the automated flow will stop replying.'}
            </Form.Text>
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleHistoryModalClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const handleStartModalClose = useCallback(() => {
    setShowStartModal(false);
  }, []);

  const createSampleTemplate = async () => {
    setLoading(true);
    try {
      const sampleTemplate = {
        name: "🌱 Energy Survey (Improved UX)",
        description: "Streamlined energy survey with natural conversation flow - reduces message fatigue and improves response rates",
        stages: [
          {
            question: "Hi! 🏡 We're conducting a quick energy efficiency survey (2 mins). Interested? Reply YES or NO",
            defaultResponse: "Please reply YES or NO to participate in our energy survey.",
            isFinal: false,
            answers: [
              {
                text: "YES",
                response: "What type of property do you live in? Reply HOUSE or APARTMENT",
                nextStageNumber: 2
              },
              {
                text: "NO", 
                response: "No worries! Contact us anytime if you change your mind. Have a great day! 😊",
                nextStageNumber: null
              }
            ]
          },
          {
            question: "What type of property do you live in? Reply HOUSE or APARTMENT",
            defaultResponse: "Please reply HOUSE or APARTMENT",
            isFinal: false,
            answers: [
              {
                text: "HOUSE",
                response: "Are you interested in solar panels for your house? Reply YES or NO",
                nextStageNumber: 3
              },
              {
                text: "APARTMENT",
                response: "Are you the owner or tenant of your apartment? Reply OWNER or TENANT",
                nextStageNumber: 4
              }
            ]
          },
          {
            question: "Are you interested in solar panels? Reply YES or NO",
            defaultResponse: "Please reply YES or NO for solar panel interest",
            isFinal: true,
            answers: [
              {
                text: "YES",
                response: "Perfect! ☀️ We'll email you solar panel information and financing options. Thanks for your time!",
                nextStageNumber: null
              },
              {
                text: "NO",
                response: "No problem! We'll send you other energy-saving tips instead. Thanks for participating! 💡",
                nextStageNumber: null
              }
            ]
          },
          {
            question: "Are you the owner or tenant? Reply OWNER or TENANT",
            defaultResponse: "Please reply OWNER or TENANT",
            isFinal: true,
            answers: [
              {
                text: "OWNER",
                response: "Great! 🔑 As an owner, you have many energy improvement options. We'll send you tailored information. Thanks!",
                nextStageNumber: null
              },
              {
                text: "TENANT",
                response: "Perfect! 🏠 We'll send you tenant-friendly energy-saving tips that don't require modifications. Thanks!",
                nextStageNumber: null
              }
            ]
          }
        ]
      };

      const response = await smsApiService.saveTemplate(sampleTemplate, instance);      if (response.data && response.data.Id) {
        showAlert('✨ Optimized template created! This version reduces message fatigue by flowing naturally from question to question without unnecessary "thank you" messages.', 'success');
        await loadTemplates(); // Reload templates to show the new one
      } else {
        showAlert('Failed to create sample template - no ID returned', 'danger');
      }
    } catch (error) {
      console.error('Error creating sample template:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.details || error.message || 'Error creating sample template';
      showAlert(errorMsg, 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleStartConversation = useCallback(async (conversationData) => {
    if (!conversationData.templateId) {
      showAlert('Please select a template first', 'danger');
      return false;
    }

    if (!conversationData.phoneNumber) {
      showAlert('Please add recipients', 'danger');
      return false;
    }

    setLoading(true);
    try {
      const selectedTemplate = templates.find(
        (template) => String(template.Id || template.id) === String(conversationData.templateId)
      );

      if (!selectedTemplate) {
        showAlert('Selected template was not found. Please re-select and try again.', 'danger');
        return false;
      }

      const templatePlaceholderTokens = extractTemplatePlaceholders(selectedTemplate);
      const nonInitialQuestionTemplateTokens = extractTemplatePlaceholdersOutsideInitialQuestion(selectedTemplate);
      const isInitialMessageOverridden = Boolean(
        conversationData.enableCustomFirstMessage && String(conversationData.firstMessageOverride || '').trim()
      );
      const baseTemplateRequiredTokens = isInitialMessageOverridden
        ? nonInitialQuestionTemplateTokens
        : templatePlaceholderTokens;
      const customFirstMessageTokens = conversationData.enableCustomFirstMessage
        ? extractPlaceholderTokensFromText(conversationData.firstMessageOverride)
        : [];
      const requiredPlaceholderTokens = Array.from(new Set([
        ...baseTemplateRequiredTokens,
        ...customFirstMessageTokens
      ]));

      const validationState = evaluateRecipientValidation({
        rawRecipientsInput: conversationData.phoneNumber,
        placeholderTokens: requiredPlaceholderTokens,
        defaultSource: {
          ...parseContextDataInput(conversationData.contextData),
          ...(conversationData.placeholderValues || {})
        }
      });

      if (!validationState.isValid) {
        if (validationState.parseError) {
          showAlert(validationState.parseError, 'danger');
        } else if (validationState.missingDefaultTokens?.length > 0 && validationState.format !== 'json') {
          showAlert(`Provide default values for all placeholders (${validationState.missingDefaultTokens.join(', ')}) or use JSON mode with placeholder values per recipient.`, 'danger');
        } else {
          showAlert('Each JSON recipient must include values for every placeholder when defaults are not fully provided.', 'danger');
        }
        return false;
      }

      if (!Array.isArray(conversationData.notifyTeamsUsers) || !conversationData.notifyTeamsUsers.length) {
        showAlert('Choose at least one person to be told in Teams before starting.', 'danger');
        return false;
      }

      const { recipients } = parseRecipientsInput(conversationData.phoneNumber, requiredPlaceholderTokens);

      if (recipients.length === 0) {
        showAlert('Please provide at least one recipient with a number', 'danger');
        return false;
      }

      const parsedContext = parseContextDataInput(conversationData.contextData);
      const placeholderContext = Object.entries(conversationData.placeholderValues || {}).reduce((acc, [key, value]) => {
        const trimmed = String(value || '').trim();
        if (trimmed) {
          acc[key] = trimmed;
        }
        return acc;
      }, {});

      const templateQuestion = getInitialStageQuestion(selectedTemplate);

      const results = [];
      const errors = [];

      // Process each recipient
      for (const recipient of recipients) {
        try {
          const contextData = {
            ...parsedContext,
            ...(recipient.additionalContext || {}),
            ...placeholderContext,
            ...(recipient.placeholderContext || {}),
            notes: conversationData.contextData || ''
          };

          // Held on the conversation rather than against the person who
          // started it, so the right people are told however long afterwards
          // the customer replies, and whoever is logged in at the time.
          if (Array.isArray(conversationData.notifyTeamsUsers) && conversationData.notifyTeamsUsers.length) {
            contextData.notifyTeamsUsers = conversationData.notifyTeamsUsers;
            // Only written when switched off. Absent means on, so older
            // conversations and the default case behave identically.
            if (conversationData.notifyOnCall === false) {
              contextData.notifyOnCall = false;
            }
            if (conversationData.notifyOnText === false) {
              contextData.notifyOnText = false;
            }
          }

          const resolvedFirstMessage = resolveTemplatePlaceholders(templateQuestion, contextData).finalMessage;
          const firstMessageOverride = conversationData.enableCustomFirstMessage
            ? ((conversationData.firstMessageOverride || '').trim() || (resolvedFirstMessage && resolvedFirstMessage !== templateQuestion ? resolvedFirstMessage : ''))
            : (resolvedFirstMessage && resolvedFirstMessage !== templateQuestion ? resolvedFirstMessage : '');

          const response = await smsApiService.startConversation(
            recipient.phoneNumber,
            parseInt(conversationData.templateId, 10),
            contextData,
            instance,
            { firstMessageOverride: firstMessageOverride || null }
          );

          if (response.data && response.data.conversationId) {
            results.push({ phone: recipient.phoneNumber, conversationId: response.data.conversationId });
          } else {
            errors.push({ phone: recipient.phoneNumber, error: 'No conversation ID returned' });
          }
        } catch (error) {
          console.error(`Error starting conversation for ${recipient.phoneNumber}:`, error);
          const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error';
          errors.push({ phone: recipient.phoneNumber, error: errorMsg });
        }
      }

      // Show detailed results
      if (results.length > 0 && errors.length === 0) {
        showAlert(`🎉 ${results.length} conversation${results.length > 1 ? 's' : ''} started successfully! Customers will receive the first message.`, 'success');
        setShowStartModal(false);
        await loadConversations();
        return true;
      } else if (results.length > 0 && errors.length > 0) {
        const errorDetails = errors.map(e => `${e.phone}: ${e.error}`).join('; ');
        showAlert(`⚠️ Partial success: ${results.length} succeeded, ${errors.length} failed. Errors: ${errorDetails}`, 'warning');
        console.error('Failed conversations:', errors);
        setShowStartModal(false);
        await loadConversations();
        return true;
      } else {
        const firstError = errors[0]?.error || 'Unknown error';
        const errorSummary = errors.length > 1 
          ? `${firstError} (and ${errors.length - 1} more - see console)` 
          : firstError;
        showAlert(`❌ All conversations failed to start. ${errorSummary}`, 'danger');
        console.error('All failed:', errors);
        return false;
      }
    } catch (error) {
      console.error('Error starting conversations:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.details || error.message || 'Error starting conversations';
      showAlert(errorMsg, 'danger');
      return false;
    } finally {
      setLoading(false);
    }
  }, [instance, templates]);

  const StartConversationModal = () => (
    <StartConversationModalMemo
      show={showStartModal}
      onHide={handleStartModalClose}
      templates={templates}
      onStart={handleStartConversation}
      loading={loading}
    />
  );

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">
                <FontAwesomeIcon icon={faComments} className="me-2" />
                SMS Conversation Manager
              </h4>
              <div>
                <Button 
                  variant="outline-primary" 
                  className="me-2"
                  onClick={loadConversations}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faRefresh} /> Refresh
                </Button>
                {isSmsAdmin && (
                  <>
                    <Button 
                      variant="outline-success" 
                      className="me-2"
                      onClick={loadTemplates}
                      disabled={loading}
                      title="Reload templates"
                    >
                      <FontAwesomeIcon icon={faRefresh} /> Templates
                    </Button>
                    <Button 
                      variant="primary"
                      onClick={() => setShowStartModal(true)}
                      disabled={templates.length === 0}
                    >
                      <FontAwesomeIcon icon={faPlay} /> Start New (Preview + Edit)
                    </Button>
                    {templates.length === 0 && (
                      <Button 
                        variant="success" 
                        className="ms-2"
                        onClick={createSampleTemplate}
                        disabled={loading}
                        title="Create optimized energy survey template with improved UX"
                      >
                        <FontAwesomeIcon icon={faPlus} /> Add Optimized Template
                      </Button>
                    )}
                  </>
                )}
              </div>
            </Card.Header>
            
            <Card.Body>
              {alert.show && (
                <Alert variant={alert.type} dismissible onClose={() => setAlert({ ...alert, show: false })}>
                  {alert.message}
                </Alert>
              )}

              {isSmsAdmin ? (
              <Alert variant="info" className="mb-3">
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faComments} className="me-2" />
                  <div>
                    <strong>📱 SMS Conversation Tips:</strong>
                    <ul className="mb-0 mt-2">
                      <li><strong>Keep it concise:</strong> Each message flows naturally to the next question</li>
                      <li><strong>No double messaging:</strong> Responses go directly to next question (no "thank you" + question)</li>
                      <li><strong>Use emojis sparingly:</strong> They make messages friendlier but keep professional</li>
                      <li><strong>Clear CTAs:</strong> Always specify exact response options (YES/NO, HOUSE/APARTMENT)</li>
                    </ul>
                  </div>
                </div>
                <small className="text-muted mt-2 d-block">
                  Templates: {templates.length} | Active Conversations: {visibleConversations.length} | Status: {loading ? 'Loading...' : 'Ready'}
                </small>
              </Alert>
              ) : (
              <Alert variant="light" className="mb-3 border">
                <div className="small text-muted">
                  Search by customer name or phone number, then open a conversation to send a human reply.
                </div>
              </Alert>
              )}

              {/* Filters */}
              <Card className="mb-3">
                <Card.Header>
                  <FontAwesomeIcon icon={faFilter} className="me-2" />
                  Filters
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small">Status</Form.Label>
                        <Dropdown autoClose="outside" className="w-100">
                          <Dropdown.Toggle
                            variant="outline-secondary"
                            className="w-100 d-flex justify-content-between align-items-center"
                            id="status-filter-toggle"
                          >
                            <span className="text-truncate">{statusFilterSummary}</span>
                          </Dropdown.Toggle>
                          <Dropdown.Menu className="w-100 px-2 py-2">
                            {SMS_STATUSES.map((item) => (
                              <Form.Check
                                key={item.value}
                                type="checkbox"
                                id={`status-filter-${item.value}`}
                                className="mb-1"
                                label={item.label}
                                checked={selectedStatuses.includes(item.value)}
                                onChange={() => toggleStatusFilter(item.value)}
                              />
                            ))}
                            <Dropdown.Divider className="my-2" />
                            <div className="d-flex gap-2">
                              <Button variant="link" size="sm" className="p-0" onClick={() => setAllStatusFilters(true)}>
                                Select all
                              </Button>
                              <Button variant="link" size="sm" className="p-0" onClick={() => setAllStatusFilters(false)}>
                                Clear
                              </Button>
                            </div>
                          </Dropdown.Menu>
                        </Dropdown>
                        <Form.Text className="text-muted">
                          {selectedStatuses.length === 0
                            ? 'Nothing ticked - showing every status.'
                            : 'Tick more than one to combine them.'}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    {isSmsAdmin && (
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small">Template</Form.Label>
                        <Form.Select
                          value={filters.templateId}
                          onChange={(e) => handleFilterChange('templateId', e.target.value)}
                        >
                          <option value="">All Templates</option>
                          {templateFilterOptions.map(template => (
                            <option key={template.Id} value={template.Id}>
                              {template.Name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    )}
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small">Customer Name / Number</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Search by customer or phone"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3} className="d-flex align-items-end">
                      <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                        Clear Filters
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Conversations Table */}
              {loading ? (
                <div className="text-center p-4">Loading conversations...</div>
              ) : (
                <>
                  <Table responsive striped hover>
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Template</th>
                        <th>Status</th>
                        <th>Started</th>
                        <th>Last Response</th>
                        <th>Messages</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleConversations.map((conversation, index) => {
                        const failure = getDeliveryFailure(conversation);
                        const optOut = getOptOut(conversation);
                        const hasError = Boolean(failure) || Boolean(optOut) || (conversation.Status === 'Error' && conversation.ErrorMessage);
                        return (
                        <tr key={conversation.Id || `conv-${index}`} className={hasError ? 'table-danger' : ''}>
                          <td>
                            <FontAwesomeIcon icon={faUser} className="me-2 text-muted" />
                            {getConversationCustomerName(conversation)}
                            <div className="text-muted small">{conversation.PhoneNumber || 'No Phone'}</div>
                            {hasError && (
                              <div className="text-danger small mt-1">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                {optOut ? `Opted out: replied "${optOut.text}"` : (failure ? `Not delivered: ${failure.reason}` : conversation.ErrorMessage)}
                              </div>
                            )}
                          </td>
                          <td>{conversation.template?.Name || 'Unknown Template'}</td>
                          <td>{getConversationBadge(conversation)}</td>
                          <td>
                            <FontAwesomeIcon icon={faCalendar} className="me-2 text-muted" />
                            {conversation.StartedAt ? formatDate(conversation.StartedAt) : 'No date'}
                          </td>
                          <td>
                            {conversation.LastResponseAt ? 
                              formatDate(conversation.LastResponseAt) : 'No activity'}
                          </td>
                          <td>
                            <Badge bg="info">{conversation.messages?.length || 0}</Badge>
                          </td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => viewConversationHistory(conversation)}
                            >
                              <FontAwesomeIcon icon={faEye} /> View
                            </Button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </Table>

                  {visibleConversations.length === 0 && (
                    <Alert variant="info" className="text-center">
                      No conversations found for the current filters.
                    </Alert>
                  )}

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-3">
                      <Pagination>
                        <Pagination.Prev 
                          disabled={currentPage <= 1}
                          onClick={() => setCurrentPage(currentPage - 1)}
                        />
                        {[...Array(totalPages)].map((_, i) => (
                          <Pagination.Item
                            key={i + 1}
                            active={i + 1 === currentPage}
                            onClick={() => setCurrentPage(i + 1)}
                          >
                            {i + 1}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next
                          disabled={currentPage >= totalPages}
                          onClick={() => setCurrentPage(currentPage + 1)}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modals */}
      {conversationHistoryModal}
      {isSmsAdmin && <StartConversationModal />}
    </Container>
  );
};

// Memoize the modal to prevent unnecessary re-renders
const StartConversationModalMemo = React.memo(({ 
  show, 
  onHide, 
  templates, 
  onStart, 
  loading 
}) => {
  const { instance } = useMsal();
  const [uploadError, setUploadError] = useState('');
  const [karmaQuery, setKarmaQuery] = useState('');
  const [karmaResults, setKarmaResults] = useState([]);
  const [karmaSearching, setKarmaSearching] = useState(false);
  const [karmaError, setKarmaError] = useState('');
  const [karmaSelected, setKarmaSelected] = useState(null);
  const [karmaSelectedNumber, setKarmaSelectedNumber] = useState('');
  // Always on since 29 Sep 2026: every conversation must name someone.
  const notifyTeams = true;
  // On by default: leaving them alone gives the behaviour people expect.
  const [notifyOnCall, setNotifyOnCall] = useState(true);
  const [notifyOnText, setNotifyOnText] = useState(true);
  const [teamsQuery, setTeamsQuery] = useState('');
  const [teamsResults, setTeamsResults] = useState([]);
  const [teamsSelected, setTeamsSelected] = useState([]);
  const [teamsSearching, setTeamsSearching] = useState(false);
  const [teamsError, setTeamsError] = useState('');
  const [teamsManualEmail, setTeamsManualEmail] = useState('');
  const [showRecipientExampleModal, setShowRecipientExampleModal] = useState(false);
  const [showAdvancedContext, setShowAdvancedContext] = useState(false);
  const [formData, setFormData] = useState({
    phoneNumber: '',
    templateId: '',
    contextData: '',
    placeholderValues: {},
    enableCustomFirstMessage: false,
    firstMessageOverride: ''
  });

  // Reset form when modal is closed
  useEffect(() => {
    if (!show) {
      setFormData({
        phoneNumber: '',
        templateId: '',
        contextData: '',
        placeholderValues: {},
        enableCustomFirstMessage: false,
        firstMessageOverride: ''
      });
      setShowAdvancedContext(false);
    }
  }, [show]);

  const handlePhoneChange = useCallback((e) => {
    if (uploadError) {
      setUploadError('');
    }
    setFormData(prev => ({ ...prev, phoneNumber: e.target.value }));
  }, [uploadError]);

  const handleTemplateChange = useCallback((e) => {
    const selectedTemplateId = e.target.value;
    const nextTemplate = templates.find(
      (template) => String(template.Id || template.id) === String(selectedTemplateId)
    );
    const tokens = extractTemplatePlaceholders(nextTemplate);
    const placeholderValues = tokens.reduce((acc, token) => {
      acc[token] = '';
      return acc;
    }, {});

    setFormData(prev => ({
      ...prev,
      templateId: selectedTemplateId,
      placeholderValues,
      enableCustomFirstMessage: false,
      firstMessageOverride: ''
    }));
  }, [templates]);

  const handleContextChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, contextData: e.target.value }));
  }, []);

  const handlePlaceholderChange = useCallback((token, value) => {
    setFormData((prev) => ({
      ...prev,
      placeholderValues: {
        ...(prev.placeholderValues || {}),
        [token]: value
      }
    }));
  }, []);

  const handleRecipientsFileUpload = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase();
    const reader = new FileReader();

    reader.onload = () => {
      try {
        const text = String(reader.result || '');
        let recipients;

        if (extension === 'json') {
          const parsed = JSON.parse(text);
          if (!Array.isArray(parsed)) {
            throw new Error('JSON file must contain an array of recipient objects.');
          }
          recipients = parsed;
        } else if (extension === 'csv') {
          recipients = parseCsvToRecipients(text);
        } else {
          throw new Error('Unsupported file type. Please upload .json or .csv');
        }

        setFormData((prev) => ({
          ...prev,
          phoneNumber: JSON.stringify(recipients, null, 2)
        }));
        setUploadError('');
      } catch (error) {
        setUploadError(error.message || 'Failed to parse uploaded file.');
      }
    };

    reader.onerror = () => {
      setUploadError('Failed to read file. Please try again.');
    };

    reader.readAsText(file);
    event.target.value = '';
  }, []);

  const handleCustomFirstMessageToggle = useCallback((e) => {
    const enabled = e.target.checked;
    setFormData(prev => ({
      ...prev,
      enableCustomFirstMessage: enabled,
      firstMessageOverride: enabled ? prev.firstMessageOverride : ''
    }));
  }, []);

  const handleFirstMessageChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, firstMessageOverride: e.target.value }));
  }, []);

  const handleStart = useCallback(async () => {
    const success = await onStart({
      ...formData,
      notifyTeamsUsers: notifyTeams ? teamsSelected : [],
      notifyOnCall,
      notifyOnText
    });
    if (success) {
      setNotifyOnCall(true);
      setNotifyOnText(true);
      setTeamsSelected([]);
      setTeamsResults([]);
      setTeamsQuery('');
      setTeamsManualEmail('');
      setTeamsError('');
      setFormData({
        phoneNumber: '',
        templateId: '',
        contextData: '',
        placeholderValues: {},
        enableCustomFirstMessage: false,
        firstMessageOverride: ''
      });
      setShowAdvancedContext(false);
    }
    // Every value read inside this callback must be listed. It previously
    // named only onStart and formData, so the Teams selection was captured
    // from whichever render last changed the form. Picking a colleague after
    // filling the form left this holding the earlier, empty list - the chips
    // showed on screen and nobody was sent to the API. It worked only when
    // something else in the form changed afterwards, which is why it looked
    // intermittent.
  }, [onStart, formData, notifyTeams, teamsSelected, notifyOnCall, notifyOnText]);

  const selectedTemplate = templates.find(
    (template) => String(template.Id || template.id) === String(formData.templateId)
  );
  const selectedTemplateStages = selectedTemplate?.stages || selectedTemplate?.Stages || [];
  const templatePlaceholderTokens = useMemo(
    () => extractTemplatePlaceholders(selectedTemplate),
    [selectedTemplate]
  );
  const nonInitialQuestionTemplateTokens = useMemo(() => {
    return extractTemplatePlaceholdersOutsideInitialQuestion(selectedTemplate);
  }, [selectedTemplate]);
  const customFirstMessageTokens = useMemo(
    () => formData.enableCustomFirstMessage ? extractPlaceholderTokensFromText(formData.firstMessageOverride) : [],
    [formData.enableCustomFirstMessage, formData.firstMessageOverride]
  );
  const baseTemplateRequiredTokens = useMemo(() => {
    const isInitialMessageOverridden = Boolean(
      formData.enableCustomFirstMessage && String(formData.firstMessageOverride || '').trim()
    );

    if (!isInitialMessageOverridden) {
      return templatePlaceholderTokens;
    }

    return nonInitialQuestionTemplateTokens;
  }, [templatePlaceholderTokens, nonInitialQuestionTemplateTokens, formData.enableCustomFirstMessage, formData.firstMessageOverride]);
  const requiredPlaceholderTokens = useMemo(
    () => Array.from(new Set([...(baseTemplateRequiredTokens || []), ...customFirstMessageTokens])),
    [baseTemplateRequiredTokens, customFirstMessageTokens]
  );
  /**
   * Counts recipients carrying a Karma contact id. Since a missing id now
   * fails quietly on the server, without this you could send a whole
   * campaign and only find out afterwards that none of it reached the CRM.
   */
  const karmaLinkSummary = useMemo(() => {
    const raw = String(formData.phoneNumber || '').trim();
    if (!raw) return null;

    try {
      const parsed = parseRecipientsInput(raw, requiredPlaceholderTokens);
      if (!parsed.recipients.length) return null;
      const linked = parsed.recipients.filter((item) => rowHasContactId(item.additionalContext)).length;
      const notMobile = parsed.recipients.filter((item) => !isUkMobile(item.phoneNumber)).length;
      return { total: parsed.recipients.length, linked, notMobile };
    } catch (error) {
      return null;
    }
  }, [formData.phoneNumber, requiredPlaceholderTokens]);

  const recipientJsonExample = useMemo(() => {
    return JSON.stringify([buildExampleRecipient(requiredPlaceholderTokens)], null, 2);
  }, [requiredPlaceholderTokens]);

  const recipientCsvExample = useMemo(() => {
    return convertRecipientsToCsv([buildExampleRecipient(requiredPlaceholderTokens)]);
  }, [requiredPlaceholderTokens]);

  // These used to bail out when no template was selected, and the buttons
  // were disabled too - so clicking them appeared to do nothing at all.
  // The example is useful before a template is chosen; it just gains extra
  // placeholder columns once one is.
  /**
   * Search Karma the same way the Booking tab does. Sitewide results often
   * omit phone numbers, so the top matches are re-fetched in full.
   */
  const handleKarmaSearch = useCallback(async () => {
    const query = String(karmaQuery || '').trim();
    if (!query) return;

    setKarmaSearching(true);
    setKarmaError('');
    setKarmaResults([]);
    setKarmaSelected(null);
    setKarmaSelectedNumber('');

    try {
      const response = await KarmaApiService.siteWideSearch(query, instance);
      const matches = normalizeKarmaResults(response?.data).slice(0, 15);

      const details = await Promise.allSettled(
        matches.map((item) => KarmaApiService.contactGet(item.id, instance))
      );

      const results = matches.map((item, idx) => {
        const full = details[idx].status === 'fulfilled' ? (details[idx].value?.data || item) : item;
        return {
          id: item.id,
          label: karmaContactName(full) || karmaContactName(item) || `Contact ${item.id}`,
          mobile: karmaPickMobile(full),
          phones: karmaPhoneOptions(full),
          address: karmaAddressPreview(full),
          address1: karmaAddressLine1(full),
          postcode: karmaPostcode(full),
          firstName: String(full?.first_name || full?.firstName || '').trim(),
          lastName: String(full?.last_name || full?.lastName || '').trim()
        };
      }).filter((item) => item.id);

      setKarmaResults(results);
      if (!results.length) {
        setKarmaError('No Karma contacts matched that search.');
      }
    } catch (error) {
      setKarmaError(error?.message || 'Could not search Karma. Please try again.');
    } finally {
      setKarmaSearching(false);
    }
  }, [karmaQuery, instance]);

  /** Pick a contact and default to its first mobile, as the Booking tab does. */
  const handleSelectKarmaContact = useCallback((contact) => {
    setKarmaSelected(contact);
    setKarmaSelectedNumber(contact?.mobile || contact?.phones?.[0]?.number || '');
  }, []);

  /**
   * Append the chosen contact to the recipient list as a JSON row, using the
   * number the user selected or typed rather than whichever one we guessed.
   */
  const handleAddKarmaRecipient = useCallback((contact, chosenNumber) => {
    const number = String(chosenNumber || '').trim();
    if (!contact?.id || !number) return;

    setFormData((prev) => {
      const raw = String(prev.phoneNumber || '').trim();
      let rows = [];

      if (raw.startsWith('[')) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) rows = parsed;
        } catch (error) {
          // Unparseable JSON in the box: start a fresh list rather than lose it silently.
          rows = [];
        }
      } else if (raw) {
        rows = raw.split(',').map((value) => value.trim()).filter(Boolean).map((number) => ({ number }));
      }

      const alreadyListed = rows.some(
        (row) => String(row?.contactId ?? '') === String(contact.id)
          && String(row?.number ?? '').replace(/\s/g, '') === number.replace(/\s/g, '')
      );
      if (alreadyListed) {
        return prev;
      }

      // Everything the Karma record can supply, so {{firstName}}, {{postcode}}
      // and friends resolve without anyone typing them in.
      const row = { contactId: contact.id, number, customerName: contact.label };
      if (contact.firstName) row.firstName = contact.firstName;
      if (contact.lastName) row.lastName = contact.lastName;
      if (contact.postcode) row.postcode = contact.postcode;
      if (contact.address1) row.address1 = contact.address1;
      rows.push(row);
      return { ...prev, phoneNumber: JSON.stringify(rows, null, 2) };
    });

    setKarmaSelected(null);
    setKarmaSelectedNumber('');
  }, []);

  const handleTeamsSearch = useCallback(async () => {
    const query = String(teamsQuery || '').trim();
    if (query.length < 2) {
      setTeamsError('Type at least two characters.');
      return;
    }

    setTeamsSearching(true);
    setTeamsError('');
    setTeamsResults([]);

    try {
      const people = await searchDirectoryPeople(instance, query);
      setTeamsResults(people);
      if (!people.length) {
        setTeamsError('Nobody matched that search.');
      }
    } catch (error) {
      setTeamsError(error?.message || 'Could not search for colleagues.');
    } finally {
      setTeamsSearching(false);
    }
  }, [teamsQuery, instance]);

  const addTeamsRecipient = useCallback((person) => {
    if (!person?.email) return;
    setTeamsSelected((prev) => (
      prev.some((item) => item.email.toLowerCase() === person.email.toLowerCase())
        ? prev
        : [...prev, { name: person.name || person.email, email: person.email }]
    ));
  }, []);

  const removeTeamsRecipient = useCallback((email) => {
    setTeamsSelected((prev) => prev.filter((item) => item.email !== email));
  }, []);

  const addTeamsManualEmail = useCallback(() => {
    const email = String(teamsManualEmail || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setTeamsError('That does not look like an email address.');
      return;
    }
    addTeamsRecipient({ name: email, email });
    setTeamsManualEmail('');
    setTeamsError('');
  }, [teamsManualEmail, addTeamsRecipient]);

  const handleDownloadJsonExample = useCallback(() => {
    triggerTextFileDownload('sms-recipients-example.json', recipientJsonExample, 'application/json;charset=utf-8');
  }, [recipientJsonExample]);

  const handleDownloadCsvExample = useCallback(() => {
    triggerTextFileDownload('sms-recipients-example.csv', recipientCsvExample, 'text/csv;charset=utf-8');
  }, [recipientCsvExample]);

  const templateMessage = getInitialStageQuestion(selectedTemplate);
  const parsedContext = parseContextDataInput(formData.contextData);
  const previewRecipientContext = useMemo(() => {
    if (!selectedTemplate || !formData.phoneNumber) {
      return {};
    }

    try {
      const parsed = parseRecipientsInput(formData.phoneNumber, requiredPlaceholderTokens);
      if (parsed.format === 'json' && parsed.recipients.length > 0) {
        return parsed.recipients[0].placeholderContext || {};
      }
    } catch (error) {
      // Keep preview based on defaults/context when JSON is invalid.
    }

    return {};
  }, [selectedTemplate, formData.phoneNumber, requiredPlaceholderTokens]);

  const combinedContext = {
    ...parsedContext,
    ...(formData.placeholderValues || {}),
    ...previewRecipientContext
  };
  const recipientValidation = useMemo(() => {
    if (!selectedTemplate) {
      return {
        isValid: false,
        parseError: null,
        recipientsCount: 0,
        missingDefaultTokens: [],
        recipientsMissingTokens: [],
        format: 'empty',
        hasAllDefaults: false,
        jsonHasFullCoverage: false
      };
    }

    return evaluateRecipientValidation({
      rawRecipientsInput: formData.phoneNumber,
      placeholderTokens: requiredPlaceholderTokens,
      defaultSource: combinedContext
    });
  }, [selectedTemplate, formData.phoneNumber, requiredPlaceholderTokens, combinedContext]);

  const resolvedTemplateMessage = resolveTemplatePlaceholders(templateMessage, combinedContext);
  const previewMessage = formData.enableCustomFirstMessage
    ? (formData.firstMessageOverride || resolvedTemplateMessage.finalMessage)
    : resolvedTemplateMessage.finalMessage;
  
  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faPlay} className="me-2" />
          Start New SMS Conversation
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Template *</Form.Label>
            <Form.Select
              value={formData.templateId}
              onChange={handleTemplateChange}
            >
              <option value="">Select a template</option>
              {templates.length > 0 ? (
                templates.map(template => (
                  <option key={template.Id || template.id} value={template.Id || template.id}>
                    {(template.Name || template.name || 'Untitled Template')} ({(template.stages || template.Stages || []).length} stages)
                  </option>
                ))
              ) : (
                <option disabled>No templates available</option>
              )}
            </Form.Select>
            {templates.length === 0 && (
              <Form.Text className="text-muted">
                No templates found. Create a template first using the SMS Template Builder or click "Add Sample Template".
              </Form.Text>
            )}
            {selectedTemplate && (
              <div className="mt-2">
                <Form.Text className="text-info d-block">
                  <strong>📋 Template:</strong> {selectedTemplate.Name || selectedTemplate.name || 'Untitled Template'}
                </Form.Text>
                <Form.Text className="text-muted d-block small">
                  {selectedTemplate.Description || selectedTemplate.description || 'No description'}
                </Form.Text>
                <div className="mt-2 p-3 bg-light rounded border-start border-primary border-4">
                  <small className="text-muted d-block mb-1">
                    <FontAwesomeIcon icon={faEye} className="me-1" />
                    First message preview (exact text that will send):
                  </small>
                  {templateMessage ? (
                    <div className="fw-bold text-dark">
                      "{previewMessage}"
                    </div>
                  ) : (
                    <Alert variant="warning" className="mb-0 py-2">
                      This template has no detectable initial stage question. Please open it in Template Builder and ensure at least one stage exists.
                    </Alert>
                  )}
                  <small className="text-muted d-block mt-2">
                    Stages detected: {Array.isArray(selectedTemplateStages) ? selectedTemplateStages.length : 0}
                  </small>
                </div>
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="custom-first-message"
              label="Override first outbound message for this send only"
              checked={formData.enableCustomFirstMessage}
              onChange={handleCustomFirstMessageToggle}
              disabled={!selectedTemplate}
            />
            <Form.Text className="text-muted">
              Use this to customize the opening SMS without changing the template.
            </Form.Text>
            {formData.enableCustomFirstMessage && (
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.firstMessageOverride}
                onChange={handleFirstMessageChange}
                placeholder="Edit the first outgoing SMS before sending"
                className="mt-2"
              />
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <Form.Label className="mb-1">Recipients *</Form.Label>
              <div className="d-flex gap-2">
                <Button
                  variant="link"
                  size="sm"
                  className="p-0"
                  onClick={() => setShowRecipientExampleModal(true)}
                >
                  Show Example
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0"
                  onClick={handleDownloadJsonExample}
                >
                  Download JSON
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0"
                  onClick={handleDownloadCsvExample}
                >
                  Download CSV
                </Button>
              </div>
            </div>
            <div className="border rounded p-2 mb-2 bg-light">
              <Form.Label className="small mb-1 fw-bold">Look up a Karma contact</Form.Label>
              <InputGroup size="sm">
                <Form.Control
                  placeholder="Search Karma by name, street or postcode"
                  value={karmaQuery}
                  onChange={(e) => setKarmaQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleKarmaSearch();
                    }
                  }}
                  disabled={!selectedTemplate}
                />
                <Button
                  variant="outline-secondary"
                  onClick={handleKarmaSearch}
                  disabled={!selectedTemplate || karmaSearching || !karmaQuery.trim()}
                >
                  {karmaSearching ? 'Searching...' : <><FontAwesomeIcon icon={faSearch} /> Search</>}
                </Button>
              </InputGroup>
              <Form.Text className="text-muted d-block">
                Adds the contact's mobile number and Karma contact ID, so replies are written back to their Karma record.
              </Form.Text>
              {karmaError && <div className="text-danger small mt-1">{karmaError}</div>}
              {karmaResults.length > 0 && (
                <div className="border rounded mt-2 bg-white" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {karmaResults.map((contact) => {
                    const isSelected = String(karmaSelected?.id ?? '') === String(contact.id);
                    const phoneCount = contact.phones?.length || 0;
                    return (
                      <div
                        key={contact.id}
                        className={`d-flex justify-content-between align-items-center px-2 py-2 border-bottom${isSelected ? ' bg-light' : ''}`}
                      >
                        <div className="me-2">
                          <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{contact.label}</div>
                          <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                            {phoneCount === 0
                              ? 'No phone numbers on this record'
                              : `${phoneCount} number${phoneCount === 1 ? '' : 's'} on record`}
                            {contact.address ? ` \u00b7 ${contact.address}` : ''}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant={isSelected ? 'primary' : 'outline-primary'}
                          disabled={phoneCount === 0}
                          onClick={() => handleSelectKarmaContact(contact)}
                        >
                          {isSelected ? 'Selected' : 'Select'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              {karmaSelected && (
                <div className="border rounded mt-2 p-2 bg-white">
                  <div className="mb-2" style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    {karmaSelected.label}
                  </div>
                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Label className="small mb-1">Select phone number</Form.Label>
                      <Form.Select
                        size="sm"
                        value={karmaSelectedNumber}
                        onChange={(e) => setKarmaSelectedNumber(e.target.value)}
                      >
                        {(karmaSelected.phones || []).map((phone) => (
                          <option key={phone.key} value={phone.number}>{phone.label}</option>
                        ))}
                        {!(karmaSelected.phones || []).some((phone) => phone.number === karmaSelectedNumber)
                          && karmaSelectedNumber
                          && <option value={karmaSelectedNumber}>{`manually entered: ${karmaSelectedNumber}`}</option>}
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <Form.Label className="small mb-1">Number to text</Form.Label>
                      <Form.Control
                        size="sm"
                        value={karmaSelectedNumber}
                        onChange={(e) => setKarmaSelectedNumber(e.target.value)}
                        placeholder="e.g. 07977133028"
                      />
                    </Col>
                  </Row>
                  <Form.Text className="text-muted d-block mt-1">
                    Choose a number from the list, or edit it manually above.
                  </Form.Text>
                  {String(karmaSelectedNumber || '').trim() && !isUkMobile(karmaSelectedNumber) && (
                    <Alert variant="warning" className="mt-2 mb-0 py-2 px-3" style={{ fontSize: '0.875rem' }}>
                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                      {karmaSelectedNumber} does not look like a UK mobile. A text to a
                      landline will not arrive, and you will still be charged for it.
                    </Alert>
                  )}
                  <div className="d-flex gap-2 mt-2">
                    <Button
                      size="sm"
                      variant={isUkMobile(karmaSelectedNumber) ? 'primary' : 'warning'}
                      disabled={!String(karmaSelectedNumber || '').trim()}
                      onClick={() => handleAddKarmaRecipient(karmaSelected, karmaSelectedNumber)}
                    >
                      {isUkMobile(karmaSelectedNumber) ? 'Add to recipients' : 'Add anyway'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => { setKarmaSelected(null); setKarmaSelectedNumber(''); }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <Form.Control
              type="file"
              accept=".json,.csv"
              className="mb-2"
              onChange={handleRecipientsFileUpload}
              disabled={!selectedTemplate}
            />
            <Form.Control
              as="textarea"
              rows={5}
              placeholder={selectedTemplate
                ? 'Use comma-separated numbers, or a JSON array with number + placeholder values.'
                : 'Select a template first to enable recipient entry'}
              value={formData.phoneNumber}
              onChange={handlePhoneChange}
              style={{ resize: 'vertical', minHeight: '120px' }}
              disabled={!selectedTemplate}
            />
            <Form.Text className="text-muted d-block">
              CSV mode: +447700900123, +447700900124 (numbers only - these will not link to Karma)
            </Form.Text>
            <Form.Text className="text-muted d-block">
              JSON mode: array of objects with contactId and number, plus placeholders and any extra context properties (case-insensitive placeholder keys).
            </Form.Text>
            <Form.Text className="text-muted d-block">
              Include <code>contactId</code> to write the conversation back to the Karma record. Download JSON or CSV above for a ready-made template.
            </Form.Text>
            {!selectedTemplate && (
              <Form.Text className="text-muted d-block fst-italic">
                Choose a template above to enable recipient entry and the Karma lookup.
              </Form.Text>
            )}
            {karmaLinkSummary && (
              <Alert
                variant={karmaLinkSummary.linked === karmaLinkSummary.total && karmaLinkSummary.notMobile === 0 ? 'success' : 'warning'}
                className="mt-2 mb-0 py-2 px-3"
                style={{ fontSize: '0.875rem' }}
              >
                <div>
                  {karmaLinkSummary.linked} of {karmaLinkSummary.total} recipient{karmaLinkSummary.total === 1 ? '' : 's'} linked to a Karma contact.
                  {karmaLinkSummary.linked < karmaLinkSummary.total
                    && ' The rest will send normally, but nothing will be written back to Karma for them.'}
                </div>
                {karmaLinkSummary.notMobile > 0 && (
                  <div className="mt-1">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    {karmaLinkSummary.notMobile} number{karmaLinkSummary.notMobile === 1 ? ' does' : 's do'} not
                    look like a UK mobile. Texts to landlines will not arrive and are still charged.
                  </div>
                )}
              </Alert>
            )}
            {uploadError && (
              <Alert variant="danger" className="mt-2 mb-0 py-2">
                {uploadError}
              </Alert>
            )}
          </Form.Group>

          {/* Who to tell when a customer replies and needs a person. The
              people are stored on the conversation, so it does not matter who
              is logged in when the reply arrives - whoever starts a flow is
              often not the one who answers it. */}
          <Form.Group className="mb-3 border rounded p-2 bg-light">
            <Form.Label className="fw-bold mb-1">Who should be told in Teams? (required)</Form.Label>
            <Form.Text className="text-muted d-block">
              Sent when the customer's reply is not one the flow expects (the conversation is then
              handed to them), when they opt out, and when the conversation is handed over.
            </Form.Text>

            {notifyTeams && (
              <div className="mt-2 ps-4">
                <Form.Check
                  type="checkbox"
                  id="notify-on-call"
                  label="Also send if the customer replies CALL"
                  checked={notifyOnCall}
                  onChange={(e) => setNotifyOnCall(e.target.checked)}
                />
                <Form.Check
                  type="checkbox"
                  id="notify-on-text"
                  label="Also send if the customer replies TEXT"
                  checked={notifyOnText}
                  onChange={(e) => setNotifyOnText(e.target.checked)}
                />
                <Form.Text className="text-muted d-block">
                  Untick either one to stop those replies notifying. Unrecognised replies and
                  hand-overs still notify whatever these are set to. The conversation itself is
                  unaffected - a CALL or TEXT reply still moves it to Open (Human Managed).
                </Form.Text>
              </div>
            )}

            {notifyTeams && (
              <div className="mt-2">
                <InputGroup size="sm">
                  <Form.Control
                    placeholder="Search colleagues by name or email"
                    value={teamsQuery}
                    onChange={(e) => setTeamsQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleTeamsSearch();
                      }
                    }}
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={handleTeamsSearch}
                    disabled={teamsSearching || teamsQuery.trim().length < 2}
                  >
                    {teamsSearching ? 'Searching...' : <><FontAwesomeIcon icon={faSearch} /> Search</>}
                  </Button>
                </InputGroup>

                {teamsError && <div className="text-danger small mt-1">{teamsError}</div>}

                {teamsResults.length > 0 && (
                  <div className="border rounded mt-2 bg-white" style={{ maxHeight: '160px', overflowY: 'auto' }}>
                    {teamsResults.map((person) => (
                      <div
                        key={person.email}
                        className="d-flex justify-content-between align-items-center px-2 py-1 border-bottom"
                      >
                        <div className="me-2">
                          <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{person.name}</div>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {person.email}{person.jobTitle ? ` \u00b7 ${person.jobTitle}` : ''}
                          </div>
                        </div>
                        <Button size="sm" variant="outline-primary" onClick={() => addTeamsRecipient(person)}>
                          Add
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <InputGroup size="sm" className="mt-2">
                  <Form.Control
                    placeholder="...or type an email address"
                    value={teamsManualEmail}
                    onChange={(e) => setTeamsManualEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTeamsManualEmail();
                      }
                    }}
                  />
                  <Button variant="outline-secondary" onClick={addTeamsManualEmail}>Add</Button>
                </InputGroup>

                {teamsSelected.length > 0 && (
                  <div className="mt-2">
                    <div className="small fw-bold mb-1">Will be notified:</div>
                    <div className="d-flex flex-wrap gap-1">
                      {teamsSelected.map((person) => (
                        <Badge bg="primary" key={person.email} className="d-flex align-items-center gap-1">
                          {person.name}
                          <span
                            role="button"
                            aria-label={`Remove ${person.name}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => removeTeamsRecipient(person.email)}
                          >
                            &times;
                          </span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {notifyTeams && teamsSelected.length === 0 && (
                  <div className="text-danger small mt-2">
                    Required: choose at least one person before starting.
                  </div>
                )}
              </div>
            )}
          </Form.Group>

          {selectedTemplate && requiredPlaceholderTokens.length > 0 && (
            <Form.Group className="mb-3">
              <Form.Label>Template Placeholders</Form.Label>
              <Form.Text className="text-muted d-block mb-2">
                Fill these values to replace placeholders in your message. Example: if your template contains {'{{firstName}}'} then enter a first name below.
              </Form.Text>
              <Row>
                {requiredPlaceholderTokens.map((token) => (
                  <Col md={6} key={token} className="mb-2">
                    <Form.Label className="small mb-1">{prettifyTokenLabel(token)}</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={`Value for {{${token}}}`}
                      value={formData.placeholderValues?.[token] || ''}
                      onChange={(e) => handlePlaceholderChange(token, e.target.value)}
                    />
                    <Form.Text className="text-muted">Token: {'{{'}{token}{'}}'}</Form.Text>
                  </Col>
                ))}
              </Row>

              {selectedTemplate && recipientValidation.parseError && (
                <Alert variant="danger" className="mt-2 mb-0 py-2">
                  {recipientValidation.parseError}
                </Alert>
              )}
              {selectedTemplate && !recipientValidation.parseError && recipientValidation.recipientsCount > 0 && !recipientValidation.isValid && (
                <Alert variant="warning" className="mt-2 mb-0 py-2">
                  {recipientValidation.missingDefaultTokens.length > 0
                    ? `Missing required placeholder values: ${recipientValidation.missingDefaultTokens.join(', ')}. Provide all defaults or switch to JSON with per-recipient values.`
                    : 'At least one JSON recipient is missing placeholder values. Add all placeholders to each recipient item.'}
                </Alert>
              )}
              {selectedTemplate && recipientValidation.isValid && (
                <Alert variant="success" className="mt-2 mb-0 py-2">
                  Validation passed: {recipientValidation.recipientsCount} recipient(s) ready.
                </Alert>
              )}
              {selectedTemplate && recipientValidation.recipientsMissingTokens.length > 0 && (
                <div className="small text-danger mt-2">
                  {recipientValidation.recipientsMissingTokens.slice(0, 3).map((item) => (
                    <div key={`recipient-missing-${item.recipientIndex}`}>
                      Recipient #{item.recipientIndex} missing: {item.missingTokens.join(', ')}
                    </div>
                  ))}
                  {recipientValidation.recipientsMissingTokens.length > 3 && (
                    <div>...and {recipientValidation.recipientsMissingTokens.length - 3} more recipient(s).</div>
                  )}
                </div>
              )}
            </Form.Group>
          )}

          <Form.Group className="mb-0">
            <Button
              variant="link"
              className="p-0 text-decoration-none"
              onClick={() => setShowAdvancedContext((prev) => !prev)}
            >
              {showAdvancedContext ? 'Hide Additional Context' : 'Add Additional Context (Optional)'}
            </Button>
            {showAdvancedContext && (
              <>
                <Form.Control
                  as="textarea"
                  rows={3}
                  className="mt-2"
                  placeholder={'Use key=value lines for extra global context.\nExample:\ncampaignName=Summer Outreach\nagentName=Rob'}
                  value={formData.contextData || ''}
                  onChange={handleContextChange}
                />
                <Form.Text className="text-muted d-block mt-1">
                  This applies to all recipients. Import files can also carry recipient-specific context columns/properties.
                </Form.Text>
              </>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleStart} 
          disabled={loading || !formData.templateId || !recipientValidation.isValid || teamsSelected.length === 0}
        >
          <FontAwesomeIcon icon={faPlay} className="me-2" />
          {loading ? 'Starting...' : 'Start Conversation'}
        </Button>
      </Modal.Footer>

      <Modal show={showRecipientExampleModal} onHide={() => setShowRecipientExampleModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Recipient JSON Example</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!selectedTemplate ? (
            <Alert variant="warning" className="mb-0">
              Select a template first to generate an example with the correct placeholders.
            </Alert>
          ) : (
            <>
              <Alert variant="info">
                Use an array of recipient objects. Keys are case-insensitive for placeholders.
              </Alert>
              <Form.Control
                as="textarea"
                rows={10}
                value={recipientJsonExample}
                readOnly
                style={{ fontFamily: 'monospace' }}
              />
              <div className="mt-3">
                <div className="small text-muted mb-1">CSV equivalent:</div>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={recipientCsvExample}
                  readOnly
                  style={{ fontFamily: 'monospace' }}
                />
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRecipientExampleModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
});

export default SMSConversationManager;